import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchSelect, Select } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { isInternalOrganisation } from '@/lib/staffGate.ts';
import { searchCohorts } from '@/lib/cohorts.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import { cohortLabel } from './cohortLabel.ts';

export interface CohortSearchSelectProps {
  /** The picked cohort itself, not its id: what the field shows is its label. */
  value: CohortWithOrganisation | null;
  onChange: (value: CohortWithOrganisation | null) => void;
  /** Narrows the list to one organisation, and drops its name from the labels. */
  organisationId?: string | null;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  className?: string;
}

/**
 * Pick one cohort by typing.
 *
 * The counterpart of `CohortPicker` below: this one asks the server for a page
 * of cohorts as the field is opened, which is what a console-wide picker needs
 * when there is no organisation to narrow it down to.
 */
export function CohortSearchSelect({
  value,
  onChange,
  organisationId,
  placeholder = 'Select cohort...',
  id,
  'aria-label': ariaLabel,
  disabled = false,
  clearable = true,
  invalid = false,
  className,
}: CohortSearchSelectProps) {
  return (
    <SearchSelect<CohortWithOrganisation>
      value={value}
      onChange={onChange}
      loadOptions={(query, signal) => searchCohorts({ query, organisationId, signal })}
      getKey={(cohort) => cohort.id}
      getLabel={(cohort) => cohortLabel(cohort, { withOrganisation: !organisationId })}
      placeholder={placeholder}
      id={id}
      aria-label={ariaLabel}
      disabled={disabled}
      clearable={clearable}
      invalid={invalid}
      className={className}
    />
  );
}

export interface CohortMultiSearchSelectProps extends Omit<
  CohortSearchSelectProps,
  'value' | 'onChange'
> {
  value: CohortWithOrganisation[];
  onChange: (value: CohortWithOrganisation[]) => void;
}

/** The same field, picking several — the broadcast and e-mail screens' shape. */
export function CohortMultiSearchSelect({
  value,
  onChange,
  organisationId,
  placeholder = 'Select cohorts...',
  id,
  'aria-label': ariaLabel,
  disabled = false,
  invalid = false,
  className,
}: CohortMultiSearchSelectProps) {
  return (
    <SearchSelect<CohortWithOrganisation>
      multiple
      value={value}
      onChange={onChange}
      loadOptions={(query, signal) => searchCohorts({ query, organisationId, signal })}
      getKey={(cohort) => cohort.id}
      getLabel={(cohort) => cohortLabel(cohort, { withOrganisation: !organisationId })}
      placeholder={placeholder}
      id={id}
      aria-label={ariaLabel}
      disabled={disabled}
      invalid={invalid}
      className={className}
    />
  );
}

export interface CohortPickerProps {
  /** Undefined is "nothing picked". */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  /** Narrows the list to one organisation. */
  organisationId?: string | null;
  /** Cohorts this screen has already used — an enrolment list, usually. */
  excludeCohortIds?: string[];
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  id?: string;
}

/**
 * Pick one cohort from a list that is short enough to read.
 *
 * A plain select over the first hundred cohorts: it sits beside an enrol
 * button, where the point is to see what there is rather than to know what to
 * search for. Cohorts are grouped under their organisation, because the same
 * group name recurs across customers.
 */
export function CohortPicker({
  value,
  onChange,
  organisationId,
  excludeCohortIds,
  disabled = false,
  placeholder = 'Select a cohort...',
  required = false,
  id,
}: CohortPickerProps) {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const { data: cohorts, isPending } = useQuery({
    queryKey: ['cohorts', 'picker', organisationId ?? null],
    queryFn: () => searchCohorts({ query: '', organisationId }),
  });

  const excluded = excludeCohortIds?.join(',') ?? '';
  const filtered = useMemo(() => {
    const ids = new Set(excluded === '' ? [] : excluded.split(','));
    return (cohorts ?? []).filter((cohort) => !ids.has(cohort.id));
  }, [cohorts, excluded]);

  const options = useMemo(() => groupedOptions(filtered), [filtered]);

  const scoped = Boolean(organisationId) && !isInternalOrganisation(user);

  return (
    <div>
      <Select
        id={id}
        wrapperClassName='w-full'
        className='w-full'
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        disabled={disabled || isPending}
        required={required}
        options={[
          { value: '', label: isPending ? 'Loading cohorts…' : placeholder },
          ...options,
        ]}
      />
      {!isPending && filtered.length === 0 && (
        <p className='mt-2 text-sm text-ink-muted'>
          No cohorts available{scoped ? ' for your organisation' : ''}
        </p>
      )}
    </div>
  );
}

/* Organisations A→Z; inside one, by track and then by cohort, no-track last. */
function groupedOptions(cohorts: CohortWithOrganisation[]) {
  const groups = new Map<string, CohortWithOrganisation[]>();
  for (const cohort of cohorts) {
    const name = cohort.organisation.name || 'Unknown Organization';
    const group = groups.get(name);
    if (group) group.push(cohort);
    else groups.set(name, [cohort]);
  }

  return [...groups.keys()].sort().map((name) => ({
    label: name,
    options: [...groups.get(name)!]
      .sort((a, b) => {
        const byTrack = (a.track?.title ?? '￿').localeCompare(b.track?.title ?? '￿');
        return byTrack === 0 ? a.title.localeCompare(b.title) : byTrack;
      })
      .map((cohort) => ({
        value: cohort.id,
        label: cohort.track ? `${cohort.track.title} - ${cohort.title}` : cohort.title,
      })),
  }));
}
